/**
 * The words of the CA firm sales deck, as data: the browser deck (main.ts, jackdesk.com/help/for-ca-firms.html) and the
 * PowerPoint (generator in ~/Videos/jackdesk-ca-demo/deck) both read this list, so the two cannot say different things.
 * Prices are computed from prices.ts through estimate.ts, never typed here.
 *
 * Rules for this file:
 *  - It is public. Case studies stay unnamed; nothing about costs or margins.
 *  - A case study says only what that customer really does, and what is still to come is labelled so (`next`).
 *  - Other companies' facts follow rivals.ts: their page is silent = null ("—"), never a "no".
 */
import { rupees } from '../prices.ts';
import { FIRMS, RIVAL, CHECKED_ON, estimate, icaiYear, taxdomeYear, zohoYear, type Rates } from './estimate.ts';

export { FIRMS, estimate };

/** Where the pictures and films live under the site. */
export const BASE = '/help/ca-firm';

/** A window picture: a real screen (`shot`, a file in BASE) or a panel drawn in panels.ts (`panel`). */
export interface Visual { shot?: string; panel?: string; phones?: string[]; alt: string; title: string }

export interface Film {
  n: number;
  title: string;
  file: string;
  poster: string;
  length: string;
  about: string;
  watch: string[];
  chapters: { label: string; at: number }[];
}

export type Slide = { id: string; label: string; notes: string } & (
  | { kind: 'cover'; kicker: string; title: string; sub: string; line: string }
  | { kind: 'pains'; eyebrow: string; title: string; lede: string; items: { q: string; a: string }[] }
  | { kind: 'cost'; eyebrow: string; title: string; lede: string; rows: { task: string; today: number; with: number }[]; foot: string }
  | { kind: 'flow'; eyebrow: string; title: string; lede: string; steps: { t: string; d: string }[]; foot: string }
  | { kind: 'map'; eyebrow: string; title: string; lede: string; tiles: { t: string; d: string; to: string; icon: string }[] }
  | { kind: 'feature'; eyebrow: string; title: string; today: string; points: string[]; visual: Visual; foot?: string; flip?: boolean }
  | { kind: 'filmsintro'; eyebrow: string; title: string; lede: string }
  | { kind: 'film'; film: Film }
  | { kind: 'trust'; eyebrow: string; title: string; lede: string; cols: { t: string; d: string }[]; before: string; after: string; foot: string }
  | { kind: 'case'; eyebrow: string; title: string; lede: string; stats: { v: string; l: string }[]; before: string[]; after: string[]; next?: { t: string; d: string }; foot: string }
  | { kind: 'compare'; eyebrow: string; title: string; lede: string; heads: string[]; rows: { label: string; cells: (string | boolean | null)[] }[]; foot: string }
  | { kind: 'calc'; eyebrow: string; title: string; foot: string }
  | { kind: 'faq'; eyebrow: string; title: string; items: { q: string; a: string }[] }
  | { kind: 'start'; eyebrow: string; title: string; lede: string; steps: { t: string; d: string }[]; offer: string }
  | { kind: 'close'; title: string; sub: string; links: { t: string; href: string }[] }
);

export const FILMS: Film[] = [
  {
    n: 1, title: 'Income-tax return of a salaried client', file: 'films/ca-film-1-salaried-itr.mp4', poster: 'films/ca-film-1.webp', length: '5:52',
    about: 'From the client’s first WhatsApp message to a return approved, paid for and signed off.',
    watch: ['Ten files sorted by themselves in minutes', 'Last year’s Form 16 set aside, a statement sent twice kept once', 'Boxes filled from the documents, both regimes side by side', 'The client approves the figures and pays by UPI on a link'],
    chapters: [{ label: 'The client asks', at: 6 }, { label: 'This year’s questions', at: 98 }, { label: 'Files ticked off', at: 128 }, { label: 'Boxes filled', at: 188 }, { label: 'Both regimes', at: 228 }, { label: 'Client approves and pays', at: 306 }],
  },
  {
    n: 2, title: 'Income-tax return of a business, with a review', file: 'films/ca-film-2-business-itr.mp4', poster: 'films/ca-film-2.webp', length: '3:43',
    about: 'A shop owner asks on WhatsApp. The reviewer sends the work back once before the partner signs.',
    watch: ['One phone number, two clients: Jackdesk asks which', 'Each file lands on the shop’s job, not the person’s', 'Two taps add the documents a new vehicle needs', 'The reviewer’s note stays on the job'],
    chapters: [{ label: 'Which client is it?', at: 14 }, { label: 'Two taps add documents', at: 94 }, { label: 'Files land on the shop', at: 116 }, { label: 'Reviewer sends it back', at: 160 }, { label: 'Filed and signed off', at: 202 }],
  },
  {
    n: 3, title: 'Monthly GST from the client’s own sheets', file: 'films/ca-film-3-gst-excel.mp4', poster: 'films/ca-film-3.webp', length: '3:47',
    about: 'The shop sends sheets from its billing software. Jackdesk reads them and computes GSTR-3B.',
    watch: ['Files land on the right month by reading inside them', 'Columns proposed once, remembered next month', 'Every bill matched with GSTR-2B', 'Only what a person must decide is asked'],
    chapters: [{ label: 'Client sends the sheets', at: 54 }, { label: 'Columns read', at: 90 }, { label: 'GSTR-2B matched', at: 108 }, { label: 'Only real questions', at: 126 }, { label: 'GSTR-3B computed', at: 160 }],
  },
  {
    n: 4, title: 'Monthly GST from Tally', file: 'films/ca-film-4-gst-tally.mp4', poster: 'films/ca-film-4.webp', length: '2:26',
    about: 'The next month, the same shop sends registers exported from Tally.',
    watch: ['Tally’s title rows skipped by themselves', 'The bill left out of September counted in October', 'September’s closing credit carried forward', 'Reviewed, filed and signed off with the ARN'],
    chapters: [{ label: 'Client sends Tally files', at: 22 }, { label: 'Tally read', at: 40 }, { label: 'Credit carried forward', at: 74 }, { label: 'GSTR-3B computed', at: 96 }, { label: 'Filed', at: 124 }],
  },
];

const mid = FIRMS.mid;
/** Meta's rates reach this file as a parameter: config.ts cannot be loaded by the plain Node tools that read this list. */
export function buildSlides(rates: Rates): Slide[] {
  const ten = estimate({ ...mid }, rates);
  return [
    {
      id: 'start', label: 'Cover', kind: 'cover', kicker: 'For CA firms',
      title: 'Every return, every client, every rupee. In one place.',
      sub: 'Your clients already send everything on WhatsApp and mail. Jackdesk turns it into tracked work: chased, filed, proved and billed.',
      line: 'Messaging · Tickets · Team chat · Projects and jobs · Workflows · Document automation · Mobile app · Workbook · Billing · Insights · Feedback',
      notes: 'Thank them for the time. Say this is 20 minutes of slides, four short films of the real thing, then their numbers in the calculator. Ask one question first: how do you know today which clients have not sent their documents?',
    },
    {
      id: 'pains', label: 'The pain', kind: 'pains', eyebrow: 'Sounds familiar?', title: 'Due-date week, every single month',
      lede: 'The work is not the hard part. The office around the work is.',
      items: [
        { q: '“Has this client sent everything?”', a: 'Staff scroll chats to find out, then ask the client again.' },
        { q: '“Is it filed or not?”', a: 'The tracker says what someone believes.' },
        { q: '“Who has this file now?”', a: 'Work moves by word of mouth.' },
        { q: '“Did anyone read that notice?”', a: 'One demand sits among thousands of routine mails.' },
        { q: '“We reminded them. They had already filed.”', a: 'Reminders go to the whole list, so clients stop reading them.' },
        { q: '“The chats left with the article.”', a: 'Client history sits on a staff member’s phone.' },
        { q: '“We did the work. Did we bill it?”', a: 'Small filings go unbilled.' },
        { q: '“Are clients happy with us?”', a: 'You find out when they move to another firm.' },
      ],
      notes: 'Do not read the list. Ask which two hurt most and go to those slides from the map. Every line here comes from what firms told us.',
    },
    {
      id: 'cost', label: 'What it costs', kind: 'cost', eyebrow: 'What it costs', title: '49 hours a month go on the office around the work',
      lede: 'An example firm: 150 clients, 100 on monthly GST.',
      rows: [
        { task: 'Keeping the filing tracker up to date', today: 20, with: 0 },
        { task: 'Chasing documents', today: 17, with: 3 },
        { task: 'Reminder rounds before due dates', today: 5, with: 0.5 },
        { task: 'Planning who does what', today: 4, with: 0 },
        { task: 'Matching acknowledgements to clients', today: 3, with: 0 },
      ],
      foot: 'The minutes are an example to replace with your own: chasing is 100 clients, 2 rounds, 5 minutes each. Hours leave out the rest: work billed that would have been forgotten, and notices answered in time.',
      notes: 'Ask them to replace the minutes with their own. The calculator near the end does this with their numbers.',
    },
    {
      id: 'flow', label: 'What changes', kind: 'flow', eyebrow: 'What changes', title: 'Your clients keep messaging. The work tracks itself.',
      lede: 'Nothing changes for the client. You still file on the portal and in your own tax software.',
      steps: [
        { t: 'Bring in clients', d: 'Upload the sheet you already keep' },
        { t: 'Work appears', d: 'Each return made as a job, 45 days ahead' },
        { t: 'Documents chased', d: 'Files ticked off as they arrive' },
        { t: 'Prepare, review, sign', d: 'Boxes filled, questions only where needed' },
        { t: 'Filed and proved', d: 'Acknowledgement read from the department’s mail' },
        { t: 'Billed', d: 'Fee shown until it is invoiced' },
        { t: 'Feedback', d: 'The client rates you on WhatsApp' },
      ],
      foot: 'Every slide ahead is one of these steps.',
      notes: 'This is the whole pitch on one slide. If they have only five minutes, stop here and go to film 1.',
    },
    {
      id: 'map', label: 'Everything in one login', kind: 'map', eyebrow: 'One login for the whole firm', title: 'Everything you run the firm on',
      lede: 'Click any tile to go to it.',
      tiles: [
        { t: 'One inbox', d: 'WhatsApp, mail, SMS, web chat', to: 'messaging', icon: 'chat' },
        { t: 'Tickets', d: 'Every request has an owner', to: 'tickets', icon: 'ticket' },
        { t: 'Team chat', d: 'Channels, calls, meetings', to: 'teamchat', icon: 'team' },
        { t: 'Projects and jobs', d: 'Every return, every period', to: 'jobs', icon: 'board' },
        { t: 'Workflows', d: 'Menus and auto replies', to: 'workflows', icon: 'flow' },
        { t: 'Document automation', d: 'Files tick themselves off', to: 'documents', icon: 'doc' },
        { t: 'From the department', d: 'Every mail read and matched', to: 'department', icon: 'mail' },
        { t: 'Mobile app', d: 'The office in your pocket', to: 'mobile', icon: 'phone' },
        { t: 'Workbook', d: 'Attendance and hours', to: 'workbook', icon: 'clock' },
        { t: 'Billing', d: 'Nothing left unbilled', to: 'billing', icon: 'rupee' },
        { t: 'Insights', d: 'The partner’s view', to: 'insights', icon: 'chart' },
        { t: 'Feedback', d: 'Know how clients feel', to: 'feedback', icon: 'star' },
      ],
      notes: 'In the browser deck each tile jumps to its slide, so you can follow what they care about instead of the order here.',
    },
    {
      id: 'messaging', label: 'One inbox', kind: 'feature', eyebrow: 'Omnichannel messaging', title: 'Every client, on the firm’s number. Not on a staff phone.',
      today: 'Clients message five different people. When someone leaves, the history leaves too.',
      points: [
        'WhatsApp, Gmail, Zoho Mail, SMS and web chat in one shared inbox',
        'Every phone and mail of a client linked to one client page',
        'Internal notes beside the chat; the client never sees them',
        'One number shared by a person and their business? Jackdesk asks which, in the client’s own words',
      ],
      visual: { shot: 'inbox.webp', alt: 'The Jackdesk inbox with a client’s income-tax chat open', title: 'Inbox · demo firm' },
      notes: 'Point at where it stands and what happens next above the chat. Anyone on the team can pick the conversation up without asking.',
    },
    {
      id: 'tickets', label: 'Tickets', kind: 'feature', flip: true, eyebrow: 'Tickets', title: 'A notice on WhatsApp becomes work with an owner and a date',
      today: 'Requests live in chats. Nobody owns them, and nothing has a due date.',
      points: [
        'Turn any message into a ticket in one click',
        'Owner, due date, where it stands, what happens next',
        'Overdue and waiting lists, so nothing sits quietly',
        'The firm’s own work too: DSC renewals, peer review papers',
      ],
      visual: { shot: 'ticket.webp', alt: 'A ticket for a notice with its owner, due date and team comments', title: 'Tickets · demo firm' },
      notes: 'Recurring returns need no ticket; they are made as jobs. Tickets are for everything else: a notice photo, a one-off request.',
    },
    {
      id: 'teamchat', label: 'Team chat', kind: 'feature', eyebrow: 'Internal team chat', title: 'Take the office out of WhatsApp groups',
      today: 'Client matters sit in personal WhatsApp groups, between family photos.',
      points: [
        'A channel for every desk, branch or project, with threads',
        'Voice and video calls and meetings in the same app',
        'Share a client’s message to a channel in one click',
        'When someone leaves, their access goes and the history stays',
      ],
      visual: { shot: 'team.webp', alt: 'Team chat with a GST desk channel and a thread', title: 'Team chat · demo firm' },
      notes: 'Menus are also in Tamil, one click away. Same login as the inbox.',
    },
    {
      id: 'jobs', label: 'Projects and jobs', kind: 'feature', flip: true, eyebrow: 'Projects and jobs', title: 'The work appears by itself, 45 days before each due date',
      today: 'Each month someone rebuilds the list of who does what, and keeps a tracker by hand.',
      points: [
        'Every service a client takes makes its own job each period',
        'Already given to who prepares, reviews and signs',
        'Filing status: each client, each month, in one word',
        '“Filed” only with the acknowledgement on it',
        'A big client gets its own project; small clients share one like “IT filing 2026”',
      ],
      visual: { panel: 'filing', alt: 'Filing status: each client against each month, and how many are left to the due date', title: 'Filing status · example' },
      notes: 'Look at the top line: 18 left, 1 day, 18 a day. The rush is seen while it can still be planned.',
    },
    {
      id: 'workflows', label: 'Workflows', kind: 'feature', eyebrow: 'Workflows', title: 'The right desk is told before anyone opens the chat',
      today: 'Every message waits for a person to read it, sort it and pass it on.',
      points: [
        'Menus and automatic replies, by your office hours',
        'GST to the GST desk, a notice to Notices',
        'Forms sent in the chat; answers saved on the client',
        'Design it yourself, by dragging boxes',
      ],
      visual: { shot: 'workflow-view.webp', alt: 'A workflow: a client messages, the firm sends a menu, each choice goes to its desk', title: 'Workflows · demo firm' },
      notes: 'Start with one workflow: the menu. Add more when the team trusts it.',
    },
    {
      id: 'documents', label: 'Document automation', kind: 'feature', flip: true, eyebrow: 'Automation of documents', title: 'Files from WhatsApp tick themselves off',
      today: 'Staff scroll chats for a bank statement, then ask the client for it again.',
      points: [
        'Each job lists what its return needs, and asks what changed this year',
        'One letter on WhatsApp asks for everything missing',
        'Every file read and matched: right client, right year, complete, not a repeat',
        'A locked PDF opens once staff type the password',
        'Boxes filled from the documents; staff review and correct',
      ],
      visual: { panel: 'documents', alt: 'A job’s documents ticked off with what was found in each file', title: 'Job documents · example' },
      foot: 'Works on PDFs, Excel sheets and portal files. Photos of bills are typed in by a person.',
      notes: 'This is the part no tracker does. Say it, then show film 1. Be plain about photos: people type those.',
    },
    {
      id: 'department', label: 'From the department', kind: 'feature', eyebrow: 'From the department', title: 'Every department mail read. No notice missed.',
      today: 'A demand notice sits among thousands of routine mails, read or not.',
      points: [
        'Income-tax mail and GSTN texts read by written rules',
        'The client found by PAN, GSTIN or TAN',
        'The acknowledgement goes on the right period as proof',
        'Notices, demands and refund failures listed with their reply-by date',
      ],
      visual: { panel: 'department', alt: 'The department’s mail, each message matched to a client with what to do next', title: 'From the department · example' },
      foot: 'Needs the firm’s email on clients’ e-filing profiles, so the mail reaches the firm.',
      notes: 'The rules were proved on real department mail: over 99% of CPC and e-filing mails recognised. No AI is involved in reading these.',
    },
    {
      id: 'reminders', label: 'Reminders', kind: 'feature', flip: true, eyebrow: 'Reminders', title: 'Reminders only to those still pending',
      today: 'A reminder goes to the whole list, and clients who already filed learn to ignore you.',
      points: [
        'Only clients still pending are reminded',
        'Every few days near the due date, from your own approved template',
        'It stops the moment the proof is on',
        'Nothing goes until the owner has seen the list and pressed start',
      ],
      visual: { shot: 'template.webp', alt: 'Sending an approved WhatsApp template, with its cost of about 12 paise shown', title: 'Templates · demo firm' },
      notes: 'See the estimated cost on the screen: about 12 paise for an update. That matters on the price slide.',
    },
    {
      id: 'films', label: 'The four films', kind: 'filmsintro', eyebrow: 'See it work', title: 'Four real runs, from the first message to signed off',
      lede: 'Recorded live on Jackdesk with a demo firm and dummy documents. No sound: we will talk you through.',
      notes: 'Pick the film closest to their work. Use the buttons under the film to jump to the part that matters, and 1.5× to go faster.',
    },
    ...FILMS.map((film): Slide => ({
      id: `film${film.n}`, label: `Film ${film.n}`, kind: 'film', film,
      notes: `${film.about} Watch for: ${film.watch.join('; ')}.`,
    })),
    {
      id: 'mobile', label: 'Mobile app', kind: 'feature', eyebrow: 'Mobile app', title: 'The office in your pocket, between meetings',
      today: 'The partner is at a client’s office. The answer is on someone’s desk.',
      points: [
        'The same inbox, tickets and team chat on Android',
        'Reply to a client on the firm’s number, from anywhere',
        'Client page, notes and feedback at hand',
        'Free on Google Play, one login with the desk',
      ],
      visual: { phones: ['phone-inbox.webp', 'phone-chat.webp'], alt: 'The Jackdesk phone app: the inbox and an open chat', title: 'Phone app · demo firm' },
      notes: 'Anyone with a staff login installs it from Google Play.',
    },
    {
      id: 'workbook', label: 'Workbook', kind: 'feature', flip: true, eyebrow: 'Workbook', title: 'Attendance that marks itself. Hours that reach the client.',
      today: 'Who came in, who worked on what: nobody knows until the month ends.',
      points: [
        'Present when work starts on the computer; no machine to punch',
        'Audit and field staff check in from their phone’s location',
        'Active, idle and overtime per person, with the trend',
        'Hours per client flow into Practice',
        'Days ready for payroll',
      ],
      visual: { panel: 'workbook', alt: 'Workbook: who is in today, with hours and the client they worked on', title: 'Workbook · example' },
      notes: 'It is a small Windows app. It signs in once and runs quietly. Tell staff what it records before you install it.',
    },
    {
      id: 'billing', label: 'Billing', kind: 'feature', eyebrow: 'Customer billing', title: 'Work done gets billed. Time spent gets seen.',
      today: 'Fees are remembered at month end, small filings go unbilled, and nobody knows what a client really takes.',
      points: [
        'Every job carries its agreed fee, with GST and SAC on the invoice',
        'Signed-off work shows as “not billed” every day until invoiced',
        'A client’s project can bill one agreed value',
        'The client approves the figures and pays by UPI link; cash is recorded too',
        'Hours by person and stage, so you see what each job took',
      ],
      visual: { panel: 'billing', alt: 'Signed off, not billed: each job with its fee and a button to bill it', title: 'Fees · example' },
      foot: 'Bill by the hour or by monthly retainer with the Workspace pack.',
      notes: 'See it in film 1: the client approves and pays on a link, and a draft receipt goes to the books.',
    },
    {
      id: 'insights', label: 'Insights', kind: 'feature', flip: true, eyebrow: 'Insights', title: 'The whole firm in one look, every morning',
      today: 'To know how things stand, the partner has to ask.',
      points: [
        'What to sign, what is late, what the department sent',
        'Money signed off but not billed',
        'On time by service, and time at each stage',
        'Reply times and overdue work by person',
        'Ask in plain words: “Which of my deliverables are late?”',
      ],
      visual: { panel: 'today', alt: 'Today for a partner: to sign, late, due soon, from the department and money not billed', title: 'Today · example' },
      notes: 'Each person gets their own Today: partner, manager, preparer, client desk, accounts.',
    },
    {
      id: 'feedback', label: 'Feedback', kind: 'feature', eyebrow: 'Client feedback', title: 'Know how clients feel before they leave',
      today: 'You hear about an unhappy client when they move to another firm.',
      points: [
        'Stars, your own questions and a comment, on WhatsApp',
        'A score on every client page, and year by year',
        'Unhappy clients flagged for someone to reply',
        'Replies go back in the same chat',
      ],
      visual: { shot: 'feedback-insights.webp', alt: 'Insights on feedback: average rating, net sentiment and how fast the team replied', title: 'Feedback · demo firm' },
      notes: 'Clients without WhatsApp get a link by mail or SMS, or staff write it down for them.',
    },
    {
      id: 'trust', label: 'Client data', kind: 'trust', eyebrow: 'Your clients’ data', title: 'Clients’ PANs and incomes never go to an AI company',
      lede: 'Most of Jackdesk runs on written rules, on our servers. AI is the small exception, and it never sees the client.',
      cols: [
        { t: 'Rules first', d: 'About 55 written patterns read the department’s mail and clients’ files. Same answer every time, no AI.' },
        { t: 'AI only for the unknown', d: 'Off unless you switch it on. It gets a masked copy, once, and what it learns becomes a rule.' },
        { t: 'Never trained on your work', d: 'AI never learns from clients’ data or computations. Photos and scans are never sent.' },
        { t: 'Your firm’s own space', d: 'Each person sees what their role allows. Chats and files belong to the firm, not to a staff phone.' },
      ],
      before: 'PAN ABCDE1234F · Income ₹8,42,500 · Ramesh Kumar',
      after: 'PAN [PAN] · Income [AMOUNT] · [NAME]',
      foot: 'The PAN and name above are made up, to show how masking works.',
      notes: 'Workpapers, figures, billing and reminders use no AI at all. Say that plainly; partners ask.',
    },
    {
      id: 'case-salem', label: 'Case study: Salem', kind: 'case', eyebrow: 'Case study · a CA firm in Salem', title: '20+ people, one view of every client conversation',
      lede: 'A mid-size firm with about 2,500 client contacts, on Jackdesk for WhatsApp and two mailboxes.',
      stats: [
        { v: '39,389', l: 'client messages' }, { v: '40,362', l: 'files received' },
        { v: '7,044', l: 'mails from the department' }, { v: '12,415', l: 'deadline reminders sent' },
      ],
      before: ['Six in ten client messages carry a file, all year', 'Someone had to read every department mail', 'Reminders went to whole lists'],
      after: [
        'The whole team answers from one inbox, on the firm’s number',
        'An eagle’s view: partners see every client chat without asking',
        'Team chat to work a matter together, beside the client',
        'Department mail read by rules: over 99% of CPC and e-filing mail recognised',
      ],
      next: { t: 'Coming next', d: 'When a notice arrives or a return fails, a workflow tells the client, so no staff member has to remember. That is how trust is built.' },
      foot: 'Counted on the firm’s own account, 17 May 2025 to 18 Oct 2026. Firm not named at our choice.',
      notes: 'This is a real firm. Do not guess its name if asked; we can arrange a call if they agree. The Coming next box is not live yet: say so if asked.',
    },
    {
      id: 'case-madurai', label: 'Case study: Madurai', kind: 'case', eyebrow: 'Case study · a company in Madurai', title: '30+ people answering customers between meetings',
      lede: 'Not a CA firm, and that is the point: any office with customers on phones and WhatsApp works the same way.',
      stats: [
        { v: '30+', l: 'people' }, { v: '7', l: 'office phones, one inbox' },
        { v: '90 min', l: 'a day back, in a worked example' },
      ],
      before: ['Customers text office SIMs held by different people', 'A reply waits until someone reaches that phone', 'Nobody sees what was said on another phone'],
      after: [
        'Each office SIM’s texts reach the shared inbox by SMS relay',
        'Staff reply from a laptop or phone, between meetings, with no SIM in hand',
        'Logged into WhatsApp in Jackdesk, to talk with customers in the same place',
        'One history for each customer, on every channel',
      ],
      foot: 'Worked example, not a measurement: 30 customer texts a day × 3 minutes to reach the right phone. Company not named at our choice.',
      notes: 'The 90 minutes is an example with its sum on the slide. Ask them how many texts a day their office SIMs get and redo the sum with them.',
    },
    {
      id: 'compare', label: 'Price comparison', kind: 'compare', eyebrow: 'Price comparison', title: `${rupees(ten.plan)} a year for a firm of ${mid.staff}, all of it`,
      lede: 'A job tracker alone costs less. ICAI’s is free for 5 users. The question is what does the work.',
      heads: ['Jackdesk', RIVAL.icai.name, RIVAL.zoho.name, `${RIVAL.taxdome.name} *`, RIVAL.interakt.name],
      rows: [
        { label: `A year, ${mid.staff} staff`, cells: [rupees(ten.plan), `about ${rupees(icaiYear(mid.staff))}`, rupees(zohoYear(mid.staff)), `about ${rupees(taxdomeYear(mid.staff))}`, rupees(RIVAL.interakt.year)] },
        { label: 'Jobs, timesheets, attendance', cells: [true, true, null, null, null] },
        { label: 'Client WhatsApp in one shared inbox', cells: [true, null, null, null, true] },
        { label: 'Client files from WhatsApp ticked off by themselves', cells: [true, null, null, null, null] },
        { label: 'Department mail read and matched to the client', cells: [true, null, null, false, null] },
        { label: 'Indian returns made as jobs before due dates', cells: [true, null, null, false, null] },
        { label: 'Team chat with calls', cells: [true, 'Chat', null, null, null] },
        { label: 'A WhatsApp update', cells: [`₹${rates.utility}, Meta’s rate`, null, null, null, `₹${RIVAL.interakt.utility.toFixed(3)}`] },
      ],
      foot: `Before GST, read on ${CHECKED_ON} from each company’s own page, except * reported on Capterra, at ₹90 a dollar. “—” means their page does not say. Jackdesk here is Messaging, CA Practice, Team chat and Workbook together.`,
      notes: 'Be the first to say a tracker costs less. Then ask: does it read the WhatsApp? Does it read the department’s mail? Check these prices again before quoting a rival, they change.',
    },
    {
      id: 'calculator', label: 'Your estimate', kind: 'calc', eyebrow: 'Your own numbers', title: 'What would it cost your firm?',
      foot: `An estimate, before GST. WhatsApp at Meta’s rate with nothing added; 1,000 replies a number a month are free. Storage assumes 100 MB a client a year, kept 3 years. Hours follow the worked example. Others’ prices read on ${CHECKED_ON}; EmpMonitor is reported on Capterra.`,
      notes: 'Ask for staff, clients, and how many on monthly GST. Type them in live. The default message counts are a fair start; a busy firm sends several thousand updates a month in season.',
    },
    {
      id: 'faq', label: 'What firms ask', kind: 'faq', eyebrow: 'What firms ask', title: 'The questions we always get',
      items: [
        { q: 'We already use tax software and Tally.', a: 'Keep them. Jackdesk runs the office around them: who does what, what is missing, what is filed, what is billed.' },
        { q: 'Does it file on the portal?', a: 'No. You file as today, and Jackdesk tracks and proves it.' },
        { q: 'What if it reads something wrong?', a: 'A file ticked off by itself can be undone in one click, and it never writes over a person’s entry.' },
        { q: 'Our clients won’t change their habits.', a: 'They don’t have to. They keep sending on WhatsApp and mail.' },
        { q: 'Is our clients’ data safe?', a: 'It stays in your firm’s own space. AI is off unless switched on, and sees only masked copies.' },
        { q: 'Do staff need training?', a: 'If they use WhatsApp, they can use the inbox. Step-by-step manuals are on jackdesk.com/help.' },
      ],
      notes: 'If asked about the law: rules are written to the law and each firm checks them before relying on them. Partners review every computation anyway.',
    },
    {
      id: 'begin', label: 'How to start', kind: 'start', eyebrow: 'Getting started', title: 'Start in an afternoon',
      lede: 'No change to how you file. No change for your clients.',
      steps: [
        { t: 'Bring your client sheet', d: 'Upload the Excel you already keep. See what each row will do before anything is written.' },
        { t: 'Tick the services', d: 'Who takes what, who prepares, reviews and signs. The jobs make themselves.' },
        { t: 'Connect WhatsApp and mail', d: 'Link each client’s phones and mails, so their files find the right work.' },
        { t: 'Run one due date', d: 'Watch Filing status and Documents through one month, then decide.' },
      ],
      offer: 'Ask for a free trial, set up on your own messages.',
      notes: 'Offer to do the first import with them. Ask for their client sheet today.',
    },
    {
      id: 'end', label: 'Thank you', kind: 'close', title: 'See it on your own clients.',
      sub: 'Bring one month’s work. We will set it up with you.',
      links: [{ t: 'Talk to us', href: '/ca' }, { t: 'See the plans', href: '/pricing' }, { t: 'Read the manuals', href: '/help' }],
      notes: 'Agree the next step before you leave: who sends the client sheet, and by when.',
    },
  ];
}
